export const WIDGET_TEST_SCRIPT_ATTR = "data-dashboard-widget-test-script";
export const WIDGET_TEST_ENV_ATTR = "data-dashboard-widget-test-environment";
export const WIDGET_TEST_LOADED_ATTR = "data-dashboard-widget-test-loaded";
export const WIDGET_TEST_SIGNATURE_ATTR = "data-dashboard-widget-test-signature";
export const WIDGET_SESSION_STATE_KEY = "convio_chatbot_state";

const WIDGET_TEST_CANCEL_EVENT = "dashboard-widget-test-cancel";
const WIDGET_TEST_INTERNAL_ATTRS = new Set([
  WIDGET_TEST_SCRIPT_ATTR,
  WIDGET_TEST_ENV_ATTR,
  WIDGET_TEST_LOADED_ATTR,
  WIDGET_TEST_SIGNATURE_ATTR,
]);

const WIDGET_RUNTIME_ARTIFACT_SELECTORS = [
  '[data-convio-widget-host="true"]',
  '[data-convio-widget-root="true"].convio-widget-root',
  "#cw-chat-widget",
  "#cw-chat-float-btn",
  "#cw-chat-widget-unavailable",
  "#chatbot_container",
];
const WIDGET_ARTIFACT_SELECTORS = [
  ...WIDGET_RUNTIME_ARTIFACT_SELECTORS,
  '[data-convio-fontawesome-fonts="true"]',
];

const getOwnerDocument = (ownerDocument) => {
  if (ownerDocument) {
    return ownerDocument;
  }

  if (typeof document !== "undefined") {
    return document;
  }

  return null;
};

const getOwnerWindow = (ownerWindow, ownerDocument) => {
  if (ownerWindow) {
    return ownerWindow;
  }

  if (ownerDocument?.defaultView) {
    return ownerDocument.defaultView;
  }

  if (typeof window !== "undefined") {
    return window;
  }

  return null;
};

const getActiveTestScript = (ownerDocument) =>
  ownerDocument.querySelector(`script[${WIDGET_TEST_SCRIPT_ATTR}]`);

const hasWidgetRuntimeDomArtifacts = (ownerDocument) =>
  WIDGET_RUNTIME_ARTIFACT_SELECTORS.some((selector) =>
    Boolean(ownerDocument.querySelector(selector))
  );

const hasWidgetRuntimeArtifacts = (ownerDocument) =>
  Boolean(getActiveTestScript(ownerDocument)) ||
  hasWidgetRuntimeDomArtifacts(ownerDocument);

const createCancelEvent = (ownerDocument) => {
  const EventCtor = ownerDocument?.defaultView?.Event || Event;
  return new EventCtor(WIDGET_TEST_CANCEL_EVENT);
};

const destroyActiveWidgetInstance = (ownerWindow) => {
  const destroy = ownerWindow?.ConvioWidget?.destroy;
  if (typeof destroy !== "function") {
    return;
  }

  try {
    destroy.call(ownerWindow.ConvioWidget);
  } catch (error) {
    // The DOM cleanup below is the fallback when a widget runtime cannot destroy itself.
  }
};

const getWidgetScriptSignature = (script) =>
  JSON.stringify(
    Array.from(script.attributes)
      .filter((attribute) => !WIDGET_TEST_INTERNAL_ATTRS.has(attribute.name))
      .map((attribute) => [attribute.name, attribute.value])
      .sort(([leftName], [rightName]) => leftName.localeCompare(rightName))
  );

const removeStoredWidgetSession = (ownerWindow) => {
  try {
    ownerWindow?.sessionStorage?.removeItem(WIDGET_SESSION_STATE_KEY);
  } catch (error) {
    // Storage can be unavailable in restricted browser contexts.
  }
};

const validateScriptSrc = (src, ownerDocument) => {
  const baseUrl =
    ownerDocument?.baseURI ||
    ownerDocument?.defaultView?.location?.href ||
    "https://dashboard.convio.ai/";

  try {
    const parsedUrl = new URL(src, baseUrl);
    if (parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:") {
      return;
    }
  } catch (error) {
    // Fall through to the common error below.
  }

  throw new Error("Widget test requires a script tag with an http(s) src attribute.");
};

const hasInlineEventHandlerAttribute = (script) =>
  Array.from(script.attributes).some((attribute) =>
    attribute.name.toLowerCase().startsWith("on")
  );

export const cleanupWidgetTestArtifacts = ({
  ownerDocument,
  ownerWindow,
} = {}) => {
  const resolvedDocument = getOwnerDocument(ownerDocument);
  if (!resolvedDocument) {
    return;
  }

  const resolvedWindow = getOwnerWindow(ownerWindow, resolvedDocument);
  if (hasWidgetRuntimeArtifacts(resolvedDocument)) {
    destroyActiveWidgetInstance(resolvedWindow);
  }

  resolvedDocument
    .querySelectorAll(`script[${WIDGET_TEST_SCRIPT_ATTR}]`)
    .forEach((element) => {
      element.dispatchEvent(createCancelEvent(resolvedDocument));
      element.remove();
    });

  WIDGET_ARTIFACT_SELECTORS.forEach((selector) => {
    resolvedDocument
      .querySelectorAll(selector)
      .forEach((element) => element.remove());
  });

  removeStoredWidgetSession(resolvedWindow);
};

export const parseWidgetScriptSnippet = (scriptHtml, ownerDocument) => {
  const resolvedDocument = getOwnerDocument(ownerDocument);
  if (!resolvedDocument || typeof scriptHtml !== "string") {
    throw new Error("Widget test requires a generated script tag.");
  }

  const template = resolvedDocument.createElement("template");
  template.innerHTML = scriptHtml.trim();

  const elementNodeType =
    resolvedDocument.defaultView?.Node?.ELEMENT_NODE || 1;
  const textNodeType = resolvedDocument.defaultView?.Node?.TEXT_NODE || 3;
  const scripts = Array.from(template.content.querySelectorAll("script"));
  const hasUnexpectedNodes = Array.from(template.content.childNodes).some(
    (node) =>
      (node.nodeType === elementNodeType &&
        node.tagName.toLowerCase() !== "script") ||
      (node.nodeType === textNodeType && node.textContent.trim() !== "")
  );

  if (scripts.length !== 1 || hasUnexpectedNodes) {
    throw new Error("Widget test requires one generated script tag.");
  }

  const sourceScript = scripts[0];
  const src = sourceScript.getAttribute("src")?.trim();
  if (!src) {
    throw new Error("Widget test requires a generated script tag with a src attribute.");
  }

  validateScriptSrc(src, resolvedDocument);

  if (sourceScript.textContent.trim()) {
    throw new Error("Widget test requires an external script tag without inline content.");
  }

  if (hasInlineEventHandlerAttribute(sourceScript)) {
    throw new Error("Widget test requires a script tag without inline event handlers.");
  }

  const script = resolvedDocument.createElement("script");
  Array.from(sourceScript.attributes).forEach((attribute) => {
    script.setAttribute(attribute.name, attribute.value);
  });
  script.setAttribute("src", src);

  if (!script.hasAttribute("default")) {
    script.setAttribute("default", "close");
  }

  return script;
};

export const testWidgetScript = ({
  environment,
  scriptHtml,
  ownerDocument,
  ownerWindow,
}) => {
  const resolvedDocument = getOwnerDocument(ownerDocument);
  if (!resolvedDocument?.body) {
    return Promise.reject(new Error("Widget test requires a page body."));
  }

  let script;
  try {
    script = parseWidgetScriptSnippet(scriptHtml, resolvedDocument);
  } catch (error) {
    if (getActiveTestScript(resolvedDocument)) {
      cleanupWidgetTestArtifacts({
        ownerDocument: resolvedDocument,
        ownerWindow,
      });
    }

    return Promise.reject(error);
  }

  const scriptSignature = getWidgetScriptSignature(script);
  const activeScript = getActiveTestScript(resolvedDocument);
  if (
    activeScript &&
    activeScript.getAttribute(WIDGET_TEST_ENV_ATTR) === environment &&
    activeScript.getAttribute(WIDGET_TEST_SIGNATURE_ATTR) === scriptSignature
  ) {
    if (!activeScript.hasAttribute(WIDGET_TEST_LOADED_ATTR)) {
      return Promise.resolve({ status: "loading" });
    }

    if (hasWidgetRuntimeDomArtifacts(resolvedDocument)) {
      return Promise.resolve({ status: "already-loaded" });
    }
  }

  cleanupWidgetTestArtifacts({
    ownerDocument: resolvedDocument,
    ownerWindow,
  });

  script.setAttribute(WIDGET_TEST_SCRIPT_ATTR, "true");
  script.setAttribute(WIDGET_TEST_ENV_ATTR, environment);
  script.setAttribute(WIDGET_TEST_SIGNATURE_ATTR, scriptSignature);

  return new Promise((resolve, reject) => {
    script.addEventListener(
      "load",
      () => {
        script.setAttribute(WIDGET_TEST_LOADED_ATTR, "true");
        resolve({ status: "loaded" });
      },
      { once: true }
    );

    script.addEventListener(
      "error",
      () => {
        script.remove();
        reject(new Error("Failed to load widget script."));
      },
      { once: true }
    );

    script.addEventListener(
      WIDGET_TEST_CANCEL_EVENT,
      () => {
        reject(new Error("Widget test canceled."));
      },
      { once: true }
    );

    resolvedDocument.body.appendChild(script);
  });
};
